import axios from 'axios';
import { SessionStatus } from './sessionState';

const SESSION_EXPIRED_ERROR_CODE = 3;

const parseResponse = (response: any): any => {
    if (typeof response !== 'string') {
        return response;
    }

    try {
        return JSON.parse(response);
    } catch {
        return null;
    }
};

const getResponseStatus = (error: any): number | undefined => {
    const response = parseResponse(error?.response);

    return response?.status || error?.xhr?.status;
};

const getResponseErrorCode = (error: any): number | undefined => {
    const response = parseResponse(error?.response);

    return response?.data?.error?.code || response?.data?.code || response?.error?.code;
};

export const isUnauthorizedResponse = (error: any): boolean => {
    const status = getResponseStatus(error);
    const errorCode = getResponseErrorCode(error);

    return (
        Number(status) === 401 ||
        Number(status) === 403 ||
        Number(errorCode) === SESSION_EXPIRED_ERROR_CODE
    );
};

export const shouldInvalidateSession = (status: SessionStatus): boolean =>
    status === SessionStatus.INITIALIZING ||
    status === SessionStatus.AUTHENTICATING ||
    status === SessionStatus.AUTHENTICATED ||
    status === SessionStatus.REVALIDATING;

const isLoginBootstrapRequest = (error: any, status: SessionStatus): boolean => {
    if (status !== SessionStatus.AUTHENTICATING) {
        return false;
    }

    const requestUrl = (error?.config?.url || '').split('?')[0];

    return requestUrl.endsWith('/session') || requestUrl.endsWith('/account/login');
};

interface UnauthorizedResponseInterceptorOptions {
    getSessionStatus: () => SessionStatus;
    onSessionExpired: (error: unknown) => void;
}

export const installUnauthorizedResponseInterceptor = ({
    getSessionStatus,
    onSessionExpired,
}: UnauthorizedResponseInterceptorOptions): (() => void) => {
    const interceptorId = axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const sessionStatus = getSessionStatus();

            if (
                isUnauthorizedResponse(error) &&
                shouldInvalidateSession(sessionStatus) &&
                !isLoginBootstrapRequest(error, sessionStatus)
            ) {
                onSessionExpired(error);
            }

            return Promise.reject(error);
        },
    );

    return () => axios.interceptors.response.eject(interceptorId);
};
